import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import {
    ChartPie, Users, AppWindow,
    Bot, Globe, ArrowLeftToLine, ArrowRightToLine, Files, ImagePlus, CreditCard
} from 'lucide-react';
import { Link, useLocation } from "react-router-dom";
import React, { useEffect } from "react";
import MenuSearch from './MenuSearch';
import PermissionGuard from '../../../components/PermissionGuard';
import './LeftSideBarAdmin.css';

import { useNavigate } from "react-router-dom";
import useRouteTracker from '../../../hooks/useRouteTracker';

function MenuGroup({ id, icon: Icon, title, items, currentPath, to }) {
    const isGroupActive = items.some(item => item.to !== "#" && currentPath.includes(item.to)) || (to && currentPath === to);
    const navigate = useNavigate();

    return (
        <div className="nav-item-wrapper">
            <a className={`nav-link dropdown-indicator label-1 ${isGroupActive ? 'active' : 'collapsed'}`}
                href={`#${id}`}
                role="button"
                data-bs-toggle="collapse"
                aria-expanded={isGroupActive ? "true" : "false"}
                aria-controls={id}
                onClick={(e) => {
                    if (to) {
                        // Let Bootstrap handle the collapse, but also navigate
                        navigate(to);
                    }
                }}
            >
                <div className="d-flex align-items-center">
                    <div className="dropdown-indicator-icon-wrapper">
                        <FontAwesomeIcon icon={faCaretRight} className="dropdown-indicator-icon" />
                    </div>
                    <span className="nav-link-icon">
                        <Icon size={19} />
                    </span>
                    <span className="nav-link-text fw-bold fs-8-1">{title}</span>
                </div>
            </a>

            <div className="parent-wrapper label-1">
                <ul className={`nav collapse parent ${isGroupActive ? 'show' : ''}`} data-bs-parent="#navbarVerticalCollapse" id={id}>
                    <li className="collapsed-nav-item-title d-none">{title}</li>
                    {items.map((item, index) => {
                        const isItemActive = item.to !== "#" && currentPath === item.to;
                        const itemContent = (
                            <li className="nav-item" key={index}>
                                <Link className={`nav-link ${isItemActive ? 'active' : ''}`} to={item.to}>
                                    <div className="d-flex align-items-center">
                                        <span className="nav-link-text fw-bold">{item.text}</span>
                                    </div>
                                </Link>
                            </li>
                        );

                        const req = item.require || "Permissions.SuperAdminOnly";
                        return (
                            <PermissionGuard key={index} require={req}>
                                {itemContent}
                            </PermissionGuard>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

function LeftSideBarAdmin() {
    useRouteTracker();
    const location = useLocation();
    const currentPath = location.pathname;
    const menuGroups = [];

    const menus = [
        { title: "Dashboard", icon: ChartPie, to: "/ai-chatbot/administrator", require: ["Permissions.DashboardAnalytics.Access", "Permissions.DashboardMetrics.Access"] },
        { title: "Main Control Panel", icon: AppWindow, to: "/ai-chatbot/administrator/main-control-panel", require: "Permissions.SuperAdminOnly" },
        { title: "User Management", icon: Users, to: "/ai-chatbot/administrator/user-management", require: ["Permissions.UserRole.Access", "Permissions.SecuritySettings.Access", "Permissions.UserVisitor.Access", "Permissions.UserInformation.Access"] },
        { title: "File Management", icon: Files, to: "/ai-chatbot/administrator/file-management", require: ["Permissions.AssetsImage.Access", "Permissions.FileManager.Access"] },
        { title: "Payment & Subscription", icon: CreditCard, to: "/ai-chatbot/administrator/payment", require: ["Permissions.PaymentInformation.Access", "Permissions.StripePayments.Access", "Permissions.UserPlanSubscription.Access"] },
        { title: "Landing Page", icon: Globe, to: "/ai-chatbot/administrator/landing-page", require: ["Permissions.LandingPageAboutUs.Access", "Permissions.LandingPageOurService.Access", "Permissions.LandingPagePrivateBlogPost.Access", "Permissions.LandingPageRoute.Access", "Permissions.LandingPageArticle.Access", "Permissions.LandingPageTerms.Access", "Permissions.LandingPageFeatureApp.Access"] },
        { title: "System Management", icon: AppWindow, to: "/ai-chatbot/administrator/system-management", require: ["Permissions.Appointment.Access", "Permissions.ApplicationParam.Access", "Permissions.Notification.Access", "Permissions.SystemJob.Access", "Permissions.GitlabPipeline.Access", "Permissions.DatabaseManagement.Access", "Permissions.EmailTemplate.Access", "Permissions.SmtpSettings.Access", "Permissions.Mailjet.Access", "Permissions.NotiInAppTemplate.Access", "Permissions.AiServicesUsage.Access", "Permissions.AiServicesPricing.Access", "Permissions.AiServicesMasterData.Access", "Permissions.AiServicesTrafficAnalytics.Access", "Permissions.ApiMonitoring.Access", "Permissions.AuditLog.Access", "Permissions.SystemException.Access"] },
        { title: "AI Chatbot Page", icon: Bot, to: "/ai-chatbot/administrator/ai-chatbot", require: ["Permissions.AwsChatbot.Access", "Permissions.QdrantSync.Access", "Permissions.ChatbotManagement.Access", "Permissions.ChatbotFeedback.Access", "Permissions.SupportTicket.Access"] }
    ];

    useEffect(() => {
        let foundTitle = "BreakThrough";

        const topLevelItem = menus.find(m => m.to === currentPath);
        if (topLevelItem) {
            foundTitle = `${topLevelItem.title} | BreakThrough`;
        } else {
            for (const group of menuGroups) {
                for (const menu of group.menus) {
                    const item = menu.items.find(i => i.to === currentPath);
                    if (item) {
                        foundTitle = `${item.text} | BreakThrough`;
                        break;
                    }
                }
                if (foundTitle !== "BreakThrough") break;
            }
        }

        document.title = foundTitle;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    const handleToggleCollapse = () => {
        const html = document.documentElement; // <html>
        html.classList.toggle("navbar-vertical-collapsed");
    }

    return (
        <nav className="navbar navbar-vertical navbar-expand-lg">
            <div className="collapse navbar-collapse" id="navbarVerticalCollapse">
                <div className="navbar-vertical-content">
                    <MenuSearch />
                    <ul className="navbar-nav flex-column" id="navbarVerticalNav">
                        {
                            menus.map((item, index) => {
                                let Icon = item.icon;
                                const isActive = currentPath === item.to || (item.to !== '/ai-chatbot/administrator' && currentPath.startsWith(item.to + '/'));
                                
                                let childRouteName = null;
                                if (isActive) {
                                    const searchParams = new URLSearchParams(location.search);
                                    const tabParam = searchParams.get('tab');
                                    const logicalPath = tabParam ? `${currentPath}/${tabParam}` : currentPath;
                                    
                                    if (logicalPath !== item.to) {
                                        const childPath = logicalPath.substring(item.to.length + 1);
                                        const segments = childPath.split('/').filter(Boolean);
                                        if (segments.length > 0) {
                                            childRouteName = segments.map(seg => seg.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(' -> ');
                                        }
                                    }
                                }

                                const itemContent = (
                                    <li className="nav-item" key={index}>
                                        <div className="nav-item-wrapper">
                                            <Link className={`nav-link label-1 ${isActive ? 'active' : ''}`} to={item.to} role="button"
                                                data-bs-toggle="" aria-expanded="false">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className={`nav-link-icon-badge ${isActive ? 'active' : ''}`}>
                                                        <Icon size={18} />
                                                    </div>
                                                    <span className="nav-link-text-wrapper d-flex flex-column justify-content-center" style={{ minWidth: 0, flex: 1 }}>
                                                        <span className="nav-link-text fs-8-1 fw-bold text-truncate">{item.title}</span>
                                                        {childRouteName && (
                                                            <span className="text-danger fw-bold nav-link-text text-truncate d-block" style={{ fontSize: '0.7rem', marginTop: '1px' }} title={childRouteName}>
                                                                &bull; {childRouteName}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </Link>
                                        </div>
                                    </li>
                                );

                                const req = item.require || "Permissions.SuperAdminOnly";
                                return (
                                    <PermissionGuard key={index} require={req}>
                                        {itemContent}
                                    </PermissionGuard>
                                );
                            })
                        }
                        {menuGroups.map((group, gIndex) => (
                            <li className="nav-item" key={gIndex}>
                                {group.groupTitle && (
                                    <>
                                        <p className="navbar-vertical-label">{group.groupTitle}</p>
                                        <hr className="navbar-vertical-line" />
                                    </>
                                )}
                                {group.menus.map((menu) => {
                                    // To determine if we should render the group at all, we can wrap it
                                    // If a menu has items with `require`, we could extract all requirements to hide the group entirely if none match.
                                    // For now, wrapping MenuGroup in PermissionGuard with ALL requirements of its items.
                                    const allRequires = menu.items.flatMap(i => i.require || []);
                                    return allRequires.length > 0 ? (
                                        <PermissionGuard key={menu.id} require={allRequires}>
                                            <MenuGroup {...menu} currentPath={currentPath} />
                                        </PermissionGuard>
                                    ) : (
                                        <MenuGroup key={menu.id} {...menu} currentPath={currentPath} />
                                    );
                                })}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="navbar-vertical-footer">
                <button onClick={handleToggleCollapse} className="btn navbar-vertical-toggle border-0 fw-semibold w-100 white-space-nowrap d-flex align-items-center">
                    <ArrowLeftToLine size={19} />
                    <ArrowRightToLine size={19} />
                    <span className="navbar-vertical-footer-text ms-2">Collapsed View</span>
                </button>
            </div>
        </nav>
    )
}

export default LeftSideBarAdmin;